<template>
  <div>
    <transition mode="out-in" name="fade" v-if="!loaded">
      <Loader />
    </transition>
    <transition
      mode="out-in"
      name="fade"
      v-else-if="canDisplay"
      v-visible="displayContent"
    >
      <App />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watchEffect, computed, Ref } from "vue";
import { adtag as reqAdtag } from "./prefetch";

import {
  provideBetData,
  provideBetDataRef
} from "@fresh8/ad-data-composition/src/betData";
import { provideInsightTestingContext } from "@fresh8/ads/src/lib/provideInjectors";
import {
  provideBrand,
  provideAdTag
} from "@fresh8/ad-data-composition/src/brand";

import { provideDisplayEvents } from "./composition/displayEvents";
import {
  useGetPublisherBetData,
  useAsyncGetPublisherBetData
} from "./hooks/useGetBetData";
import { Defaults as potentialWinningsDefaults } from "@fresh8/ad-data-composition/src/usePotentialWinnings";

import { awaitPromise } from "./lib/awaitPromise";

import { BetData } from "@fresh8/types/src/betData";
import {
  AdTag,
  AdTagExperimentVariant,
  BackgroundImageSetMap
} from "@fresh8/types/src/index";
import { provideTheme } from "@fresh8/ad-data-composition/src/theme";
import { providePreview } from "@fresh8/ad-data-composition/src/preview";
import { providePreroll } from "./hooks/usePreroll";

import App from "./App.vue";
import Loader from "./components/Loader.vue";
import { createJssContext, provideJssContext } from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";
import { Preview } from "@fresh8/types/src/preview";
import { provideFeatureFlag } from "./hooks/useFeatureFlag";
import properties from "./properties";
import { Operator, AdTagCampaign } from "@fresh8/types/src";
import { randomCampaign, randomOperator } from "./lib/ramdomise";

export default defineComponent({
  name: "AppWrapper",
  components: {
    App,
    Loader
  },
  setup() {
    const displayContent = ref(false);
    const betDataContext: {
      betData: Ref<BetData | undefined>;
      get?(
        ...args
      ): {
        result: Ref<BetData | undefined>;
        loading: Ref<boolean>;
        error: Ref<boolean>;
      };
      loading: Ref<boolean>;
    } = {
      betData: ref(),
      loading: ref(false)
    };

    const {
      result: adTag,
      loading: adTagLoading,
      error: adTagError
    } = awaitPromise<AdTag>(reqAdtag);

    const betResult = ref<BetData>();
    const betDataLoading = ref<boolean>(true);
    const betDataError = ref<boolean>(false);

    const selectedOperator = ref<Operator | undefined>(undefined);
    const selectedCampaign = ref<AdTagCampaign | undefined>(undefined);
    const selectedCampaignPreroll = computed(() => {
      return selectedCampaign.value?.campaignContent?.preroll;
    });

    watchEffect(async () => {
      /**
       * checks if the feature flag exists and is enabled
       * if yes, it gets the bet data from the new publishers api
       * if not, it sets the useGetBetData result.
       * */

      if (!adTag.value || betResult.value) return;

      // operator and campaign selection
      const op = adTag.value.operator?._id
        ? adTag.value.operator
        : randomOperator(adTag.value.operators || []);
      selectedOperator.value = op;
      selectedCampaign.value = randomCampaign(
        adTag.value.campaigns || [],
        adTag.value.classification,
        op || ({} as Operator)
      );

      const betDataResultAdServer = await useAsyncGetPublisherBetData(
        adTag,
        selectedOperator,
        selectedCampaign
      );

      // if the campaign selected has a targetUrl we need to set it to the iframe as well
      const targetUrl = selectedCampaign.value?.targetUrl;

      if (targetUrl && betDataResultAdServer.result) {
        betDataResultAdServer.result.prerollTargetUrl = targetUrl;
        betDataResultAdServer.result.targetUrl = targetUrl;
      }

      // if the batDataResult came without a operator id, get it from the adtag.
      if (
        betDataResultAdServer.result &&
        !betDataResultAdServer.result.operator?._id
      ) {
        const _id = adTag.value?.operator?._id,
          name = adTag.value?.operator.key,
          friendlyName = adTag.value?.operator.name;

        betDataResultAdServer.result.operator = {
          ...betDataResultAdServer.result.operator,
          _id,
          name,
          friendlyName
        };
      }

      betResult.value = betDataResultAdServer.result;
      if (betDataResultAdServer.error)
        betResult.value = { betQuery: {}, ...betResult.value } as BetData;

      betDataLoading.value = betDataResultAdServer.loading;
      betDataError.value = betDataResultAdServer.error;
      betDataContext.get = useGetPublisherBetData;

      displayContent.value = true;
      setTimeout(() => {
        displayContent.value = true;
      }, 1000);
    });

    watchEffect(() => {
      if (betResult.value) {
        betDataContext.betData.value = betResult.value;

        if (adTag.value && betResult.value.operator?.name) {
          adTag.value.operator = {
            key: betResult.value.operator.name,
            name: betResult.value.operator.friendlyName || "",
            _id: betResult.value.operator._id
          };
        }

        //     setTimeout(() => {
        //       displayContent.value = true;
        //     }, 1000);
      }
    });

    const jssContext = createJssContext({});

    const brand = computed(() => adTag.value?.brand);
    const preview = computed(() => ({ isPreview: false } as Preview));
    const tracking = computed(() => adTag.value?.tracking);
    const team = computed(() => brand.value?.team);
    const macros = computed(() => adTag.value?.macros || []);

    const stake = computed(() => {
      let stake = potentialWinningsDefaults.stake;

      if (brand.value?.adTemplate?.potentialWinnings?.stake) {
        stake = brand.value.adTemplate.potentialWinnings.stake;
      }

      return stake;
    });

    const insightRandomTesting = computed<boolean>(() =>
      !brand.value?.adTemplate?.insights?.enabledAbTesting
        ? true
        : Math.random() < 0.5
    );

    const insightTestingContextProvider = computed(() => ({
      insightRandomTesting: insightRandomTesting.value
    }));

    const adTagExperiments = computed(() => adTag.value?.experiments);

    /** Experiment selected to show its offer */
    const adTagExperiment = computed(() => {
      // returns an empty object when the experiments don't exist
      if ((adTagExperiments.value?.length || 0) === 0) return;

      // if we have a campaign selected, we randomly choose to show an experiment or not
      const useExperiment = selectedCampaign.value
        ? !!Math.floor(Math.random() * 2)
        : true;

      if (!useExperiment) return;

      // new we filter the valid experiments, and if we have no valid experiments, we return undefined.
      const validExperiments = (adTagExperiments.value || []).filter(e => {
        return !!e.variants[0].operators[selectedOperator.value?._id || ""];
      });

      if (validExperiments.length === 0) return;

      // between all valid experiments we choose one randomly.
      const selectedExperiment =
        validExperiments[Math.floor(Math.random() * validExperiments.length)];

      return selectedExperiment;
    });

    /** represents the selected experiment variant, if we have an experiment at all. */
    const adTagExperimentVariant = computed<AdTagExperimentVariant | undefined>(
      () => {
        if (!adTagExperiment.value) return;

        const selectedVariant =
          adTagExperiment.value.variants[
            Math.floor(Math.random() * adTagExperiment.value.variants.length)
          ];

        return selectedVariant;
      }
    );

    provideInsightTestingContext(insightTestingContextProvider);
    provideBetDataRef(betDataContext);
    provideFeatureFlag();
    provideJssContext(jssContext);
    provideAdTag(adTag, selectedOperator, selectedCampaign);
    provideBetData(
      betDataContext.betData,
      tracking,
      team,
      macros,
      stake,
      selectedOperator,
      adTagExperimentVariant,
      selectedCampaign
    );
    provideBrand(brand);
    providePreview(preview);

    // setup prerolls
    const sizeId = computed(() => adTag?.value?.size?._id);
    const prerollImageSets = computed(() => {
      return selectedCampaignPreroll.value
        ? ({
            default: selectedCampaignPreroll.value?.prerollImageSet
            // preview: selectedCampaignPreroll.value?.prerollImageSet
          } as BackgroundImageSetMap)
        : brand.value?.prerollImageSets;
    });
    const brandPreroll = computed(() => brand.value?.adTemplate?.preroll);

    providePreroll(
      brandPreroll,
      sizeId,
      prerollImageSets,
      selectedCampaignPreroll
    );

    const analyticsContext = createAnalyticsContext(
      betDataContext.betData,
      adTag,
      properties.statsURL,
      preview,
      adTagExperiment,
      adTagExperimentVariant,
      selectedCampaign
    );

    provideAnalyticsContext(analyticsContext);
    provideDisplayEvents(
      betDataContext.betData,
      computed(() => brand.value?.adTemplate?.eventCardOrder || [])
    );
    provideTheme(ref({}));

    watchEffect(() => {
      if (adTagError.value) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      }
    });

    const loaded = computed(
      () => !adTagLoading.value && !betDataLoading.value && !adTagError.value
    );

    return {
      loaded,
      canDisplay: computed(() => !!adTag.value?.brand),
      displayContent
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
